import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowRight, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Reveal, Container, ParallaxImg } from "@/components/common/Primitives";
import { PageHero, SectionHead, Rows, Kicker, Headline, Lead, RuleDraw, ClosingBand, HGallery, Success, Field, EditorialRow, INPUT_CLS, TEXTAREA_CLS } from "@/components/common/Editorial";
import { PHONE } from "@/components/layout/Header";
import { api, errorCode } from "@/lib/api";
import { useLang, errorText } from "@/lib/i18n";
import { useContent } from "@/lib/content";
import { usePageMeta } from "@/lib/seo";
import { SIZES } from "@/lib/images";

/* copy (binding – memory/copy_deck_nynorsk.md §4) */
/** The restaurant film from the hotel's current site, re-encoded and served locally (public/videos). */
const RESTAURANT_VIDEO = {
  mp41080: "/videos/restaurant-1080.mp4",
  mp4720: "/videos/restaurant-720.mp4",
  webm720: "/videos/restaurant-720.webm",
};
const OFFER_POS = { lunch: "50% 60%", dinner: "50% 50%", five: "50% 50%", tasting: "50% 30%" };
const MEAL = [
  { id: "lunch", nn: "Lunsj", en: "Lunch" },
  { id: "dinner", nn: "Middag", en: "Dinner" },
  { id: "tasting", nn: "Smaking", en: "Tasting" },
];

export default function Dining() {
  const { lang, tx, tr } = useLang();
  const { data } = useContent();
  const d = data?.dining;
  usePageMeta(tx("Restaurant Baronen", "Restaurant Baronen"), tx("Lunsj med fjordutsikt eller ein middag det er verdt å setje av tid til. I Baronen lagar me maten frå botnen av, med råvarer frå Rosendal og Hardanger.", "Lunch with a fjord view or a dinner worth setting aside time for. At Baronen we make everything from scratch, with produce from Rosendal and Hardanger."));
  const offers = d?.offers || [];
  const [form, setForm] = useState({ name: "", email: "", phone: "", date: "", time: "", guests: "", meal: "", message: "", website: "" });
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  useEffect(() => {
    if (window.location.hash === "#bord") setTimeout(() => document.getElementById("bord")?.scrollIntoView({ behavior: "smooth" }), 400);
  }, []);
  const toTable = (meal) => { if (meal) set("meal", meal); document.getElementById("bord")?.scrollIntoView({ behavior: "smooth" }); };
  const mealLabel = (id) => { const m = MEAL.find((x) => x.id === id); return m ? (lang === "en" ? m.en : m.nn) : ""; };
  const send = useMutation({
    mutationFn: async () => (await api.post("/inquiries", {
      kind: "contact", topic: tx("Bordførespurnad", "Table request"),
      name: form.name, email: form.email, phone: form.phone,
      date: [form.date, form.time].filter(Boolean).join(" "),
      guests: form.guests ? parseInt(form.guests, 10) : null,
      serving: mealLabel(form.meal), message: form.message, website: form.website, lang,
    })).data,
    onSuccess: () => { setDone(true); toast.success(tx("Takk! Førespurnaden er send.", "Thank you! Your request has been sent.")); },
    onError: (err) => toast.error(errorText(errorCode(err), lang)),
  });

  const hoursRows = (d?.hours || []).map((h) => [tr(h.label), h.value]);
  const gallery = [
    { src: "/images/hotel-glass-sun.jpg", title: tx("Lunsj i glasdelen", "Lunch in the glass pavilion"), position: "50% 60%" },
    { src: "/images/restaurant-service.jpg", title: tx("Bord på terrassen", "A table on the terrace"), tall: true },
    { src: "/images/restaurant-hall-wide.jpg", title: tx("Kveld i Baronen", "An evening at Baronen") },
    { src: "/images/chef-plating.jpg", title: tx("Frå kjøkenet", "From the kitchen"), tall: true, position: "50% 40%" },
    { src: "/images/dish-salmon.jpg", title: tx("Råvarer frå Hardanger", "Produce from Hardanger") },
  ];
  const offerMeal = { lunch: "lunch", dinner: "dinner", five: "dinner", tasting: "tasting" };

  return (
    <div data-testid="dining-page">
      <PageHero
        testid="dining-hero"
        image="/images/restaurant-video-poster.jpg"
        position="50% 60%"
        video={RESTAURANT_VIDEO}
        videoLabel={tx("Film frå Restaurant Baronen – salen med båten i taket, servering, frukost og glasdelen mot fjorden", "Film from Restaurant Baronen – the hall with the boat in the ceiling, service, breakfast and the glass pavilion facing the fjord")}
        height="h-[88svh] min-h-[600px] max-h-[1000px]"
        alt={tx("Restaurant Baronen med dekka bord, båten i taket og utsikt mot fjorden", "Restaurant Baronen with set tables, the boat in the ceiling and a view of the fjord")}
        kicker={tx("Restaurant Baronen", "Restaurant Baronen")}
        title={tx("Velkomen til bords", "Welcome to the table")}
        lead={tx("Lunsj med fjordutsikt eller ein middag det er verdt å setje av tid til. I Baronen lagar me maten frå botnen av, med vekt på råvarer frå Rosendal og Hardanger.", "Lunch with a fjord view or a dinner worth setting aside time for. At Baronen we make everything from scratch, with produce from Rosendal and Hardanger at the centre.")}
      >
        <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3">
          <Button size="lg" variant="inverse" onClick={() => toTable()} data-testid="dining-book-table">{tx("Spør om bord", "Ask about a table")} <ArrowRight className="h-4 w-4" strokeWidth={1.5} /></Button>
          <p className="hero-shadow max-w-md text-sm leading-relaxed text-white/80">{tx("Send oss ønskt dato og tidspunkt. Bordet er bestilt når du har fått stadfesting frå oss.", "Send us your preferred date and time. The table is booked once you have received our confirmation.")}</p>
        </div>
      </PageHero>

      {/* Lunch, dinner, tasting – editorial rows */}
      <section className="pt-24 lg:pt-32" data-testid="dining-offers">
        <Container>
          <Reveal><SectionHead kicker={tx("Lunsj, middag og smaking", "Lunch, dinner and tastings")} title={tx("Mat laga frå botnen av", "Food made from scratch")} /></Reveal>
          <div className="mt-12 space-y-20 lg:mt-16 lg:space-y-28">
            {offers.map((o, i) => (
              <Reveal key={o.id} data-testid={`dining-offer-${o.id}`}>
                <EditorialRow src={o.image} position={OFFER_POS[o.id] || "50% 50%"} index={i + 1} label={tr(o.when)} title={tr(o.title)} body={tr(o.text)} flip={i % 2 === 1} eager={i === 0}>
                  <div className="mt-7">
                    <Button variant="outline" onClick={() => toTable(offerMeal[o.id])} data-testid={`dining-offer-${o.id}-cta`}>{tr(o.cta) || tx("Spør om bord", "Ask about a table")} <ArrowRight className="h-4 w-4" strokeWidth={1.5} /></Button>
                  </div>
                </EditorialRow>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* Hours */}
      <section className="pt-24 lg:pt-32" data-testid="dining-hours">
        <Container>
          <RuleDraw className="mb-16 lg:mb-24" />
          <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
            <Reveal className="lg:col-span-5">
              <Kicker className="mb-5">{tx("Opningstider", "Opening hours")}</Kicker>
              <Headline>{tx("Når vil du kome?", "When would you like to come?")}</Headline>
              <Lead>{tr(d?.hours_note)}</Lead>
              <Button size="lg" className="mt-8" onClick={() => toTable()} data-testid="dining-ask-table">{tx("Spør om bord", "Ask about a table")} <ArrowRight className="h-4 w-4" strokeWidth={1.5} /></Button>
            </Reveal>
            <Reveal delay={0.05} className="lg:col-span-7 lg:pt-2">
              <Rows rows={hoursRows} testid="dining-hours-list" labelWidth="sm:grid-cols-[1fr_auto]" size="lg" />
            </Reveal>
          </div>
        </Container>
      </section>

      {/* Celebrations */}
      <section className="pt-24 lg:pt-32" data-testid="dining-party">
        <Container>
          <RuleDraw className="mb-16 lg:mb-24" />
          <div className="grid items-center gap-10 lg:grid-cols-12 lg:gap-16">
            <Reveal className="lg:col-span-6">
              <ParallaxImg src="/images/hall-banquet.jpg" alt={tx("Dekka langbord i selskapslokalet", "A long table set in the function room")} className="aspect-[4/3]" position="50% 50%" range={4} sizes={SIZES.half} />
            </Reveal>
            <Reveal delay={0.05} className="lg:col-span-5 lg:col-start-8">
              <Kicker className="mb-5">{tx("Selskap", "Celebrations")}</Kicker>
              <Headline>{tx("Skal de samlast rundt bordet?", "Gathering around the table?")}</Headline>
              <Lead>{tx("Planlegg de ein bursdag, eit jubileum eller ein middag for fleire? Fortel oss kor mange de blir, så lagar me eit forslag til lokale og servering.", "Planning a birthday, an anniversary or a dinner for a group? Tell us how many you will be and we will draft a proposal for the venue and the food.")}</Lead>
              <Button asChild size="lg" variant="outline" className="mt-8" data-testid="dining-party-cta"><Link to="/arrangement?type=birthday#forespurnad">{tx("Spør om selskap", "Ask about a celebration")} <ArrowRight className="h-4 w-4" strokeWidth={1.5} /></Link></Button>
            </Reveal>
          </div>
        </Container>
      </section>

      {/* Gallery */}
      <section className="pt-24 lg:pt-32" data-testid="dining-gallery-section">
        <Container>
          <RuleDraw className="mb-16 lg:mb-24" />
          <Reveal><SectionHead kicker={tx("Galleri", "Gallery")} title={tx("Frå Baronen", "From Baronen")} testid="dining-gallery-title" /></Reveal>
          <Reveal delay={0.05} className="mt-12 lg:mt-16"><HGallery items={gallery} testid="dining-gallery" /></Reveal>
        </Container>
      </section>

      {/* Table request */}
      <section id="bord" className="scroll-mt-24 py-24 lg:py-32" data-testid="dining-table-request">
        <Container>
          <RuleDraw className="mb-16 lg:mb-24" />
          <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
            <div className="lg:col-span-5">
              <Reveal className="lg:sticky lg:top-28">
                <Kicker className="mb-5">{tx("Bordførespurnad", "Table request")}</Kicker>
                <Headline>{tx("Spør om bord", "Ask about a table")}</Headline>
                <Lead>{tx("Vel dato og tidspunkt, og fortel oss kor mange de blir. Me tek kontakt med svar på førespurnaden.", "Choose a date and time, and tell us how many you will be. We will get back to you with a reply.")}</Lead>
                <div className="mt-8 border-t border-border pt-6 text-[0.9375rem] text-muted-foreground">
                  <p>{tx("Vil du heller ringje?", "Would you rather call?")} <a href={PHONE.tel} className="font-medium text-foreground underline decoration-border underline-offset-4 hover:decoration-foreground tabular">{PHONE.display}</a></p>
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              {done ? (
                <Success testid="dining-table-success" title={tx("Takk! Førespurnaden er send.", "Thank you! Your request has been sent.")} body={tx("Me tek kontakt med svar på førespurnaden. Bordet er først bestilt når du har fått stadfesting frå oss.", "We will get back to you with a reply. The table is only booked once you have received our confirmation.")} />
              ) : (
                <form onSubmit={(e) => { e.preventDefault(); send.mutate(); }} className="grid gap-6 sm:grid-cols-2" data-testid="dining-table-form">
                  <Field id="t-name" label={tx("Namn", "Name")}><Input id="t-name" required value={form.name} onChange={(e) => set("name", e.target.value)} className={INPUT_CLS} autoComplete="name" data-testid="table-name" /></Field>
                  <Field id="t-email" label={tx("E-postadresse", "Email address")}><Input id="t-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className={INPUT_CLS} autoComplete="email" data-testid="table-email" /></Field>
                  <Field id="t-phone" label={tx("Telefon", "Phone")}><Input id="t-phone" type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={INPUT_CLS} autoComplete="tel" data-testid="table-phone" /></Field>
                  <Field id="t-guests" label={tx("Tal på gjester", "Number of guests")}><Input id="t-guests" type="number" min="1" inputMode="numeric" required value={form.guests} onChange={(e) => set("guests", e.target.value)} className={INPUT_CLS} data-testid="table-guests" /></Field>
                  <Field id="t-date" label={tx("Ønskt dato", "Preferred date")}><Input id="t-date" type="date" required value={form.date} onChange={(e) => set("date", e.target.value)} className={INPUT_CLS} data-testid="table-date" /></Field>
                  <Field id="t-time" label={tx("Ønskt tidspunkt", "Preferred time")}><Input id="t-time" type="time" value={form.time} onChange={(e) => set("time", e.target.value)} className={INPUT_CLS} data-testid="table-time" /></Field>
                  <Field id="t-meal" label={tx("Lunsj, middag eller smaking", "Lunch, dinner or tasting")} className="sm:col-span-2">
                    <Select value={form.meal || "unset"} onValueChange={(v) => set("meal", v === "unset" ? "" : v)}>
                      <SelectTrigger id="t-meal" className={INPUT_CLS} data-testid="table-meal"><SelectValue placeholder={tx("Vel", "Choose")} /></SelectTrigger>
                      <SelectContent className="rounded-lg">
                        <SelectItem value="unset">{tx("Vel", "Choose")}</SelectItem>
                        {MEAL.map((m) => <SelectItem key={m.id} value={m.id}>{lang === "en" ? m.en : m.nn}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field id="t-message" label={tx("Allergiar eller andre omsyn (valfritt)", "Allergies or other considerations (optional)")} className="sm:col-span-2"><Textarea id="t-message" rows={4} value={form.message} onChange={(e) => set("message", e.target.value.slice(0, 1400))} className={TEXTAREA_CLS} data-testid="table-message" /></Field>
                  <input type="text" value={form.website} onChange={(e) => set("website", e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
                  <div className="flex flex-col gap-5 border-t border-border pt-6 sm:col-span-2 sm:flex-row sm:items-center sm:justify-between">
                    <p className="max-w-md text-xs leading-relaxed text-muted-foreground">{tx("Me brukar opplysningane til å behandle førespurnaden.", "We use your details to handle the request.")} <Link to="/praktisk#vilkar" className="underline decoration-border underline-offset-4 hover:decoration-foreground">{tx("Les personvernerklæringa", "Read the privacy policy")}</Link></p>
                    <Button type="submit" size="lg" disabled={send.isPending} data-testid="table-submit">{send.isPending ? tx("Sender …", "Sending …") : tx("Send bordførespurnad", "Send table request")} <ArrowRight className="h-4 w-4" strokeWidth={1.5} /></Button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </Container>
      </section>

      <ClosingBand
        testid="dining-closing"
        title={tx("Vil du bli natta over?", "Staying the night?")}
        lead={tx("Sjå ledige rom for datoen du vil ete hos oss. Frukost er inkludert i alle hotellrom.", "See available rooms for the date you want to dine with us. Breakfast is included in all hotel rooms.")}
        image="/images/room-fjord-balcony.jpg"
        position="50% 50%"
      >
        <Button asChild size="lg" variant="inverse" data-testid="dining-closing-cta"><Link to="/bestill">{tx("Sjå ledige rom", "See available rooms")} <ArrowRight className="h-4 w-4" strokeWidth={1.5} /></Link></Button>
        <Button asChild size="lg" variant="outline-light" className="tabular" data-testid="dining-closing-phone"><a href={PHONE.tel}><Phone className="h-4 w-4" strokeWidth={1.5} /> {tx("Ring", "Call")} {PHONE.display}</a></Button>
      </ClosingBand>
    </div>
  );
}
